import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { Alert } from 'src/app/models/alert';
import { AlertType } from 'src/app/models/alert-type';
import { AlertService } from 'src/app/services/alert.service';

type AlertClass =
  'alert-success' | 'alert-danger' | 'alert-warning' | 'alert-info';

type AlertIconClass =
  | 'freshicon-approved-green'
  | 'freshicon-alert-circle-red'
  | 'freshicon-alert-triangle-orange'
  | 'freshicon-info-blue';

const ALERT_TYPE_CLASSES: Record<AlertType, [AlertClass, AlertIconClass]> = {
  [AlertType.Success]: ['alert-success', 'freshicon-approved-green'],
  [AlertType.Error]: ['alert-danger', 'freshicon-alert-circle-red'],
  [AlertType.Warning]: ['alert-warning', 'freshicon-alert-triangle-orange'],
  [AlertType.Info]: ['alert-info', 'freshicon-info-blue']
};

const DISMISS_DELAY_MS = 3000;
const ERROR_DISMISS_DELAY_MS = 5000;
const HOVER_EXIT_DELAY_MS = 1500;

@Component({
  selector: 'app-alert',
  templateUrl: './alert.component.html',
  styleUrls: ['./alert.component.scss'],
  imports: [NgbAlert],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AlertComponent {
  private readonly alertService$ = inject(AlertService);
  private readonly wrapper = viewChild<ElementRef<HTMLElement>>('wrapper');

  private dismissTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private paused = false;

  readonly visible = signal(false);
  readonly alert = signal<Alert | null>(null);

  readonly alertClass = computed<AlertClass>(
    () => ALERT_TYPE_CLASSES[this.alert()?.type ?? AlertType.Info][0]
  );

  readonly alertIconClass = computed<AlertIconClass>(
    () => ALERT_TYPE_CLASSES[this.alert()?.type ?? AlertType.Info][1]
  );

  constructor() {
    this.alertService$.pipe(takeUntilDestroyed()).subscribe((alert: Alert) => {
      this.resetState();
      this.visible.set(true);
      this.alert.set(alert);

      const delayMs =
        alert.type === AlertType.Error
          ? ERROR_DISMISS_DELAY_MS
          : DISMISS_DELAY_MS;

      // Defer to next tick so DOM is rendered before checking hover state
      setTimeout(() => this.scheduleAutoDismiss(delayMs), 0);
    });
  }

  onMouseEnter(): void {
    if (!this.paused && this.visible()) {
      this.paused = true;
      this.clearDismissTimeout();
    }
  }

  onMouseLeave(): void {
    if (this.paused) {
      this.paused = false;
      this.scheduleAutoDismiss(HOVER_EXIT_DELAY_MS);
    }
  }

  close(): void {
    this.resetState();
    this.visible.set(false);
  }

  private scheduleAutoDismiss(delayMs: number): void {
    if (this.wrapper()?.nativeElement.matches(':hover')) {
      this.paused = true;
      return;
    }

    this.dismissTimeoutId = setTimeout(() => {
      if (!this.paused) {
        this.visible.set(false);
      }
    }, delayMs);
  }

  private resetState(): void {
    this.clearDismissTimeout();
    this.paused = false;
  }

  private clearDismissTimeout(): void {
    if (this.dismissTimeoutId) {
      clearTimeout(this.dismissTimeoutId);
      this.dismissTimeoutId = null;
    }
  }
}
